import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Room, RoomEvent } from 'livekit-client';
import { streamApi } from '../utils/api';
import { initSocket, joinStream, sendChatMessage } from '../utils/socket';

export default function ViewerPage() {
  const { slug } = useParams();
  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const roomRef = useRef(null);
  const chatContainerRef = useRef(null);
  
  const [user, setUser] = useState(null);
  const [isLive, setIsLive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [streamer, setStreamer] = useState(null);
  const [streamTitle, setStreamTitle] = useState('');
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  const [viewerCount, setViewerCount] = useState(0);
  const [streamDuration, setStreamDuration] = useState(0);
  const [streamStartedAt, setStreamStartedAt] = useState(null);
  const [isMuted, setIsMuted] = useState(true);
  const [volume, setVolume] = useState(0.8);
  const [isChatOpen, setIsChatOpen] = useState(true);
  const [isChatFloating, setIsChatFloating] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [autoScroll, setAutoScroll] = useState(true);
  const [messages, setMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);
  const [showEmojis, setShowEmojis] = useState(false);
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const [highlightedUsers, setHighlightedUsers] = useState(new Set());
  const [recentMessages, setRecentMessages] = useState([]); // For fullscreen overlay
  const videoContainerRef = useRef(null);
  const chatInputRef = useRef(null);
  
  const emojis = ['😀', '😂', '❤️', '🔥', '👏', '🎉', '😍', '🤔', '😢', '😮', '👍', '👎', '💯', '🚀', '⭐', '💎'];
  const reactions = [
    { icon: '👍', label: 'Like' },
    { icon: '👏', label: 'Clap' },
    { icon: '❤️', label: 'Love' },
    { icon: '🔥', label: 'Fire' },
    { icon: '😂', label: 'LOL' }
  ];
  
  const LIVEKIT_URL = 'wss://cineclubs.cloud:7882';

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try { setUser(JSON.parse(storedUser)); } catch (e) {}
    }
  }, []);
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      // Fix for mobile viewport height (accounts for browser toolbar)
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // iOS Safari keyboard fix - BULLETPROOF approach
  // Lock html/body on mount, use Visual Viewport API exclusively
  const mainContentRef = useRef(null);
  
  useEffect(() => {
    if (!isMobile || typeof window === 'undefined') return;
    
    const html = document.documentElement;
    const body = document.body;
    
    // Save original styles
    const originalHtmlStyle = html.getAttribute('style') || '';
    const originalBodyStyle = body.getAttribute('style') || '';
    
    // STEP 1: Lock html/body to kill iOS bounce completely
    html.style.cssText = 'position: fixed; overflow: hidden; width: 100%; height: 100%; top: 0; left: 0;';
    body.style.cssText = 'position: fixed; overflow: hidden; width: 100%; height: 100%; top: 0; left: 0;';
    
    // STEP 2: Force scroll to 0 on ANY scroll event
    const forceScrollToTop = () => {
      window.scrollTo(0, 0);
    };
    window.addEventListener('scroll', forceScrollToTop, { passive: false });
    
    // STEP 3: Prevent touchmove except inside chat-messages
    const preventBounce = (e) => {
      const chatMessages = document.querySelector('.chat-messages');
      if (chatMessages && chatMessages.contains(e.target)) {
        // Allow scroll inside chat, but contain overscroll
        const scrollTop = chatMessages.scrollTop;
        const scrollHeight = chatMessages.scrollHeight;
        const clientHeight = chatMessages.clientHeight;
        
        // At top, trying to scroll up
        if (scrollTop <= 0) {
          const touchY = e.touches[0]?.clientY;
          if (touchY && e.targetTouches.length === 1) {
            // Let it through, but set scrollTop to 1 to prevent bounce
            chatMessages.scrollTop = 1;
          }
        }
        // At bottom, trying to scroll down
        if (scrollTop + clientHeight >= scrollHeight - 1) {
          chatMessages.scrollTop = scrollHeight - clientHeight - 1;
        }
        return;
      }
      e.preventDefault();
    };
    document.addEventListener('touchmove', preventBounce, { passive: false });
    
    // STEP 4: Visual Viewport API for keyboard detection and height sync
    let lastKeyboardState = false;
    
    const updateLayout = () => {
      const vv = window.visualViewport;
      if (!vv) return;
      
      const visibleHeight = vv.height;
      const offsetTop = vv.offsetTop;
      
      // Always reset scroll
      window.scrollTo(0, 0);
      
      // Update CSS variable
      html.style.setProperty('--visible-height', `${visibleHeight}px`);
      html.style.setProperty('--viewport-offset', `${offsetTop}px`);
      
      // Nav height is 56px on mobile
      const navHeight = 56;
      
      // Apply height and position directly to main-content via ref
      if (mainContentRef.current) {
        const contentHeight = visibleHeight - navHeight;
        mainContentRef.current.style.height = `${contentHeight}px`;
        // Keep top fixed at nav height - don't add offsetTop
        mainContentRef.current.style.top = `${navHeight}px`;
      }
      
      // Detect keyboard - compare with window.innerHeight
      const windowHeight = window.innerHeight;
      const diff = windowHeight - visibleHeight;
      const keyboardNowOpen = diff > 150;
      
      // Always update state and trigger re-layout
      if (keyboardNowOpen !== lastKeyboardState) {
        lastKeyboardState = keyboardNowOpen;
        setIsKeyboardOpen(keyboardNowOpen);
        setKeyboardHeight(keyboardNowOpen ? diff : 0);
        
        if (keyboardNowOpen) {
          // Auto-scroll chat to bottom when keyboard opens
          setTimeout(() => {
            if (chatContainerRef.current) {
              chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
            }
          }, 50);
        }
      }
      
      // Force re-apply layout even if state didn't change
      // This handles cases where keyboard reopens quickly
      if (keyboardNowOpen && mainContentRef.current) {
        const contentHeight = visibleHeight - navHeight;
        mainContentRef.current.style.height = `${contentHeight}px`;
      }
    };
    
    // Initialize
    if (window.visualViewport) {
      updateLayout();
      window.visualViewport.addEventListener('resize', updateLayout);
      window.visualViewport.addEventListener('scroll', updateLayout);
    }
    window.addEventListener('resize', updateLayout);
    
    // Cleanup
    return () => {
      window.removeEventListener('scroll', forceScrollToTop);
      document.removeEventListener('touchmove', preventBounce);
      window.removeEventListener('resize', updateLayout);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', updateLayout);
        window.visualViewport.removeEventListener('scroll', updateLayout);
      }
      // Restore original styles
      html.setAttribute('style', originalHtmlStyle);
      body.setAttribute('style', originalBodyStyle);
    };
  }, [isMobile, isKeyboardOpen]);
  
  useEffect(() => {
    if (!slug) return;
    const fetchStatus = async () => {
      try {
        const { data } = await streamApi.getStatus(slug);
        if (data.success) {
          setIsLive(data.is_live);
          if (data.stream) {
            setStreamer(data.stream.streamer);
            setStreamTitle(data.stream.title);
            if (data.stream.started_at) setStreamStartedAt(new Date(data.stream.started_at));
          }
        }
      } catch (err) { console.error('Failed to fetch stream:', err); }
      finally { setLoading(false); }
    };
    fetchStatus();
  }, [slug]);
  
  useEffect(() => {
    if (!slug) return;
    const loadMessages = async () => {
      try {
        const { data } = await streamApi.getChatMessages(slug);
        if (data.success && data.messages) {
          setMessages(data.messages.map(m => ({
            id: m.id, username: m.username, message: m.message,
            isStreamer: m.is_owner || m.user_id === data.owner_id,
            timestamp: new Date(m.created_at).getTime()
          })));
          // Scroll to bottom after loading messages
          setTimeout(() => {
            if (chatContainerRef.current) {
              chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
            }
          }, 100);
        }
      } catch (e) { console.error('Failed to load chat:', e); }
    };
    loadMessages();
  }, [slug]);
  
  useEffect(() => {
    if (!slug) return;
    const connectToLiveKit = async () => {
      try {
        setConnectionStatus('connecting');
        const { data } = await streamApi.getLivekitToken(slug, false);
        if (!data.success || !data.token) { setConnectionStatus('waiting'); return; }
        
        const room = new Room({ adaptiveStream: true, dynacast: true });
        room.on(RoomEvent.TrackSubscribed, (track) => {
          if (track.kind === 'video' && videoRef.current) {
            track.attach(videoRef.current);
            setIsLive(true);
            setConnectionStatus('connected');
          }
          if (track.kind === 'audio' && audioRef.current) {
            track.attach(audioRef.current);
            // Start muted - user must click unmute (browser autoplay policy)
            audioRef.current.muted = true;
            audioRef.current.volume = volume;
            setIsMuted(true);
          }
        });
        room.on(RoomEvent.TrackUnsubscribed, (track) => track.detach());
        room.on(RoomEvent.ParticipantConnected, () => setViewerCount(room.remoteParticipants.size + 1));
        room.on(RoomEvent.ParticipantDisconnected, () => setViewerCount(room.remoteParticipants.size + 1));
        room.on(RoomEvent.Disconnected, () => { setIsLive(false); setConnectionStatus('disconnected'); });
        
        await room.connect(LIVEKIT_URL, data.token);
        roomRef.current = room;
        
        room.remoteParticipants.forEach(p => {
          p.trackPublications.forEach(pub => {
            if (pub.isSubscribed && pub.track) {
              if (pub.track.kind === 'video' && videoRef.current) { pub.track.attach(videoRef.current); setIsLive(true); }
              if (pub.track.kind === 'audio' && audioRef.current) { 
                pub.track.attach(audioRef.current); 
                audioRef.current.muted = true;
                audioRef.current.volume = volume;
                setIsMuted(true);
              }
            }
          });
        });
      } catch (err) { console.error('LiveKit error:', err); setConnectionStatus('error'); }
    };
    connectToLiveKit();
    return () => { if (roomRef.current) { roomRef.current.disconnect(); roomRef.current = null; } };
  }, [slug]);
  
  useEffect(() => {
    if (!slug) return;
    const socket = initSocket();
    if (user) joinStream(slug, user, 'viewer');
    socket.on('viewers:update', ({ count }) => setViewerCount(count));
    socket.on('stream:started', () => { setIsLive(true); window.location.reload(); });
    socket.on('stream:ended', () => { setIsLive(false); if (roomRef.current) { roomRef.current.disconnect(); roomRef.current = null; } });
    socket.on('chat:message', (msg) => {
      const newMsg = { ...msg, id: msg.id || Date.now(), timestamp: Date.now() };
      setMessages(prev => [...prev.slice(-199), newMsg]);
      if (!isChatOpen) setUnreadCount(c => c + 1);
      if (autoScroll && chatContainerRef.current) setTimeout(() => { chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight; }, 50);
      
      // Add to recent messages for fullscreen overlay (auto-remove after 5 seconds)
      setRecentMessages(prev => [...prev.slice(-4), { ...newMsg, fadeId: Date.now() }]);
      setTimeout(() => {
        setRecentMessages(prev => prev.filter(m => m.fadeId !== newMsg.fadeId));
      }, 5000);
    });
    socket.on('user:highlighted', ({ username, highlighted }) => {
      setHighlightedUsers(prev => {
        const newSet = new Set(prev);
        if (highlighted) newSet.add(username);
        else newSet.delete(username);
        return newSet;
      });
    });
    return () => { socket.off('viewers:update'); socket.off('stream:started'); socket.off('stream:ended'); socket.off('chat:message'); socket.off('user:highlighted'); };
  }, [slug, user, isChatOpen, autoScroll]);
  
  useEffect(() => {
    if (!isLive || !streamStartedAt) return;
    const updateDuration = () => setStreamDuration(Math.floor((new Date() - streamStartedAt) / 1000));
    updateDuration();
    const interval = setInterval(updateDuration, 1000);
    return () => clearInterval(interval);
  }, [isLive, streamStartedAt]);

  // Fullscreen change listener
  useEffect(() => {
    const handleFullscreenChange = () => {
      const video = videoRef.current;
      const isFs = !!(document.fullscreenElement || document.webkitFullscreenElement || (video && video.webkitDisplayingFullscreen));
      setIsFullscreen(isFs);
    };
    
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    
    // iOS video fullscreen events
    const video = videoRef.current;
    if (video) {
      video.addEventListener('webkitbeginfullscreen', handleFullscreenChange);
      video.addEventListener('webkitendfullscreen', handleFullscreenChange);
    }
    
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      if (video) {
        video.removeEventListener('webkitbeginfullscreen', handleFullscreenChange);
        video.removeEventListener('webkitendfullscreen', handleFullscreenChange);
      }
    };
  }, []);

  const formatDuration = (s) => {
    const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
    return h > 0 ? `${h}:${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}` : `${m}:${sec.toString().padStart(2, '0')}`;
  };
  const formatTime = (ts) => new Date(ts).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });

  const toggleMute = useCallback(() => {
    setIsMuted(prev => {
      const newMuted = !prev;
      if (audioRef.current) { 
        audioRef.current.muted = newMuted; 
        if (!newMuted) { 
          audioRef.current.volume = volume; 
          audioRef.current.play().catch(() => {}); 
        } 
      }
      // Also update all LiveKit audio tracks
      if (roomRef.current) {
        roomRef.current.remoteParticipants.forEach(p => {
          p.audioTrackPublications.forEach(pub => {
            if (pub.track) {
              const elements = pub.track.attachedElements;
              elements.forEach(el => {
                el.muted = newMuted;
                if (!newMuted) {
                  el.volume = volume;
                  el.play().catch(() => {});
                }
              });
            }
          });
        });
      }
      return newMuted;
    });
  }, [volume]);

  const handleVolumeChange = (e) => {
    const val = parseFloat(e.target.value);
    setVolume(val);
    setIsMuted(val === 0);
    
    // Update audio element
    if (audioRef.current) { 
      audioRef.current.volume = val; 
      audioRef.current.muted = val === 0; 
    }
    
    // Also update all audio elements in the room (for LiveKit)
    if (roomRef.current) {
      roomRef.current.remoteParticipants.forEach(p => {
        p.audioTrackPublications.forEach(pub => {
          if (pub.track) {
            const elements = pub.track.attachedElements;
            elements.forEach(el => {
              el.volume = val;
              el.muted = val === 0;
            });
          }
        });
      });
    }
  };

  // Fullscreen toggle - works on both mobile and desktop
  const toggleFullscreen = useCallback(async () => {
    const video = videoRef.current;
    const container = videoContainerRef.current;
    
    console.log('toggleFullscreen called, video:', !!video, 'container:', !!container);
    
    // Check if already in fullscreen
    const isCurrentlyFullscreen = !!(document.fullscreenElement || document.webkitFullscreenElement || (video && video.webkitDisplayingFullscreen));
    
    console.log('isCurrentlyFullscreen:', isCurrentlyFullscreen);
    
    if (!isCurrentlyFullscreen) {
      // ENTER FULLSCREEN
      
      // iOS Safari - use native video fullscreen
      if (video && typeof video.webkitEnterFullscreen === 'function') {
        console.log('Trying iOS native fullscreen');
        try {
          // Make sure video is playing first (required for iOS)
          if (video.paused) {
            console.log('Video paused, trying to play');
            await video.play().catch((e) => console.log('Play error:', e));
          }
          video.webkitEnterFullscreen();
          console.log('webkitEnterFullscreen called');
          return;
        } catch (e) {
          console.log('iOS fullscreen failed:', e);
        }
      }
      
      // Desktop/Android - use container fullscreen
      if (container) {
        console.log('Trying container fullscreen');
        if (container.requestFullscreen) {
          await container.requestFullscreen().catch((e) => console.log('requestFullscreen error:', e));
        } else if (container.webkitRequestFullscreen) {
          container.webkitRequestFullscreen();
        }
      }
    } else {
      // EXIT FULLSCREEN
      console.log('Exiting fullscreen');
      if (document.exitFullscreen) {
        await document.exitFullscreen().catch(() => {});
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      }
    }
  }, []);

  const togglePause = useCallback(() => {
    if (videoRef.current) {
      if (videoRef.current.paused) {
        videoRef.current.play();
        setIsPaused(false);
      } else {
        videoRef.current.pause();
        setIsPaused(true);
      }
    }
  }, []);

  const handleVideoClick = useCallback(() => {
    togglePause();
  }, [togglePause]);

  // Keyboard shortcuts - must be after function definitions
  useEffect(() => {
    const handleKeydown = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      switch(e.key.toLowerCase()) {
        case 'm': toggleMute(); break;
        case 'c': setIsChatOpen(prev => !prev); break;
        case 'f': toggleFullscreen(); break;
        case ' ': e.preventDefault(); togglePause(); break;
      }
    };
    window.addEventListener('keydown', handleKeydown);
    return () => window.removeEventListener('keydown', handleKeydown);
  }, [toggleMute, toggleFullscreen, togglePause]);

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (!chatInput.trim() || !user) return;
    sendChatMessage(chatInput.trim());
    setChatInput('');
    setShowEmojis(false);
  };

  const addEmoji = (emoji) => setChatInput(prev => prev + emoji);
  const handleToggleChat = () => { setIsChatOpen(!isChatOpen); if (!isChatOpen) setUnreadCount(0); };

  if (loading) {
    return (<div className="streamer-page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}><div className="spinner"></div><style>{styles}</style></div>);
  }

  return (
    <div className={`streamer-page ${isFullscreen ? 'fullscreen' : ''}`}>
      <audio ref={audioRef} autoPlay playsInline />
      
      {/* Hide nav when fullscreen */}
      {!isFullscreen && (
      <nav className="top-nav">
        <div className="nav-left">
          <a href="/live/streams" className="logo"><img src="/public/assets/images/cineclubs.png" alt="CineClubs" /><div className="logo-text"><span className="logo-title"><span className="text-white">CINE</span><span className="text-red">CLUBS</span></span><span className="logo-subtitle">Your Private Cinema</span></div></a>
          {isLive && (<div className="live-indicator"><span className="pulse-dot"></span><span>LIVE</span><span className="duration">{formatDuration(streamDuration)}</span></div>)}
        </div>
        <div className="nav-center">
          <div className="title-wrapper" style={{ cursor: 'default' }}>
            <span style={{ flex: 1, color: 'white', fontSize: '0.95rem' }}>{streamTitle || (streamer?.username ? `${streamer.username}'s Stream` : 'Live Stream')}</span>
          </div>
        </div>
        <div className="nav-right">
          <div className="stat-item"><i className="fas fa-eye"></i><span>{viewerCount}</span></div>
          {!isMobile && <button className={`nav-btn chat-btn ${unreadCount > 0 ? 'has-unread' : ''}`} onClick={handleToggleChat}><i className="fas fa-comments"></i>{unreadCount > 0 && <span className="badge">{unreadCount}</span>}</button>}
        </div>
      </nav>
      )}

      {/* Main Content */}
      <main ref={mainContentRef} className={`main-content ${isChatOpen && !isChatFloating ? 'with-chat' : ''} ${isMobile ? 'mobile-layout' : ''} ${isMobile && isChatOpen ? 'chat-visible' : ''} ${isKeyboardOpen ? 'keyboard-open' : ''}`}>
        <section className="video-section">
          {/* Video Container - gets fullscreen class when needed */}
          <div className={`video-container ${isFullscreen ? 'custom-fullscreen' : ''}`} ref={videoContainerRef} onClick={handleVideoClick}>
            {/* Video element always exists */}
            <video 
              ref={videoRef} 
              autoPlay 
              playsInline 
              muted
              controls={false}
              className={`preview-video ${!isLive ? 'hidden' : ''}`}
            />
            
            {isLive ? (
              <>
                
                {/* Fullscreen Chat Overlay */}
                {isFullscreen && recentMessages.length > 0 && (
                  <div className="fullscreen-chat-overlay">
                    {recentMessages.map((msg) => (
                      <div key={msg.fadeId} className="overlay-message">
                        <span className="overlay-username">{msg.username}</span>
                        <span className="overlay-text">{msg.message}</span>
                      </div>
                ))}
              </div>
            )}
            
            {/* Video controls - always visible */}
              <div className="video-controls" onClick={(e) => e.stopPropagation()}>
                <div className="controls-left">
                  <button className="ctrl-btn" onClick={togglePause}>
                    <i className={`fas ${isPaused ? 'fa-play' : 'fa-pause'}`}></i>
                  </button>
                  {!isMobile && (
                    <div className="volume-control">
                      <button className={`ctrl-btn small ${isMuted ? 'off' : ''}`} onClick={toggleMute}><i className={`fas ${isMuted ? 'fa-volume-mute' : 'fa-volume-up'}`}></i></button>
                      <input type="range" min="0" max="1" step="0.01" value={isMuted ? 0 : volume} onChange={handleVolumeChange} className="volume-slider" />
                    </div>
                  )}
                  {isMobile && (
                    <button className={`ctrl-btn ${isMuted ? 'off' : ''}`} onClick={toggleMute}><i className={`fas ${isMuted ? 'fa-volume-mute' : 'fa-volume-up'}`}></i></button>
                  )}
                </div>
                <div className="controls-center"></div>
                <div className="controls-right">
                  {!isMobile && <button className={`ctrl-btn ${isChatFloating ? 'active' : ''}`} onClick={() => setIsChatFloating(!isChatFloating)}><i className="fas fa-window-restore"></i></button>}
                  <button className="ctrl-btn" onClick={toggleFullscreen}><i className={`fas ${isFullscreen ? 'fa-compress' : 'fa-expand'}`}></i></button>
                </div>
              </div>
              </>
            ) : (
              <div className="camera-off">
                <div className="camera-icon"><i className={`fas ${connectionStatus === 'connecting' ? 'fa-spinner fa-spin' : 'fa-broadcast-tower'}`}></i></div>
                <p>{connectionStatus === 'connecting' ? 'Connecting...' : 'Stream Offline'}</p>
                {streamer && (<div className="streamer-badge"><img src={streamer.avatar || '/cinebadge.png'} alt="" /><span>{streamer.username}</span></div>)}
              </div>
            )}
          </div>
          
          {/* Stream info - hide when fullscreen */}
          {!isFullscreen && !isMobile && (
            <div className="stream-info">
              <div className="streamer-info">
                <img src={streamer?.avatar || '/cinebadge.png'} alt="" className="avatar" />
                <div className="info-text"><h3>{streamer?.username || 'Streamer'}</h3><span className={`status ${isLive ? 'live' : ''}`}>{isLive ? '🔴 Currently Live' : '⚫ Offline'}</span></div>
              </div>
              <div className="stream-actions">
                <button className="action-btn" onClick={() => navigator.clipboard.writeText(`https://cineclubs.cloud/live/${slug}`)}><i className="fas fa-share-alt"></i><span>Share</span></button>
              </div>
            </div>
          )}
        </section>

        {/* Chat - hide when fullscreen */}
        {!isFullscreen && (
        <aside className={`chat-section ${isChatOpen ? 'open' : 'closed'} ${isChatFloating ? 'floating' : ''} ${isMobile ? 'mobile' : ''}`}>
          <div className="chat-header">
            <div className="chat-tabs"><button className="tab active"><i className="fas fa-comments"></i> Chat</button></div>
            <div className="chat-actions">
              <button className={`small-btn ${autoScroll ? 'active' : ''}`} onClick={() => setAutoScroll(!autoScroll)}><i className="fas fa-arrow-down"></i></button>
              {!isMobile && <button className="small-btn" onClick={() => setIsChatFloating(!isChatFloating)}><i className="fas fa-external-link-alt"></i></button>}
              {!isMobile && <button className="small-btn close" onClick={() => setIsChatOpen(false)}><i className="fas fa-times"></i></button>}
            </div>
          </div>
          
          <div className="chat-messages" ref={chatContainerRef}>
            {messages.length === 0 ? (<div className="chat-empty"><i className="fas fa-comment-dots"></i><p>No messages yet</p><span>Chat messages will appear here</span></div>) : (
              messages.map((msg) => (
                <div key={msg.id} className={`message ${msg.isStreamer ? 'streamer' : ''} ${highlightedUsers.has(msg.username) ? 'user-highlighted' : ''}`}>
                  <div className="msg-header">
                    <span className="msg-badges">
                      {msg.isStreamer && <span className="badge-icon host">🎬</span>}
                      {highlightedUsers.has(msg.username) && <span className="badge-icon highlight">✨</span>}
                    </span>
                    <span className={`msg-author ${highlightedUsers.has(msg.username) ? 'highlighted-user' : ''}`}>{msg.username}</span>
                    <span className="msg-time">{formatTime(msg.timestamp)}</span>
                  </div>
                  <div className="msg-content">{msg.message}</div>
                </div>
              ))
            )}
          </div>
          
          <div className="reactions-bar">
            {reactions.map((r, i) => (<button key={i} className="reaction-btn" onClick={() => user && sendChatMessage(r.icon)} disabled={!user}>{r.icon}</button>))}
          </div>
          
          <form className="chat-form" onSubmit={handleSendMessage}>
            {user ? (
              <>
                <div className="input-wrapper">
                  <button type="button" className="emoji-btn" onClick={() => setShowEmojis(!showEmojis)}><i className="fas fa-smile"></i></button>
                  <input 
                    ref={chatInputRef}
                    type="text" 
                    value={chatInput} 
                    onChange={(e) => setChatInput(e.target.value)} 
                    placeholder="Send a message..." 
                    maxLength={500}
                    onFocus={() => isMobile && setIsKeyboardOpen(true)}
                    onBlur={() => isMobile && setTimeout(() => setIsKeyboardOpen(false), 100)}
                  />
                  <button type="submit" className="send-btn" disabled={!chatInput.trim()}><i className="fas fa-paper-plane"></i></button>
                </div>
                {showEmojis && (<div className="emoji-picker">{emojis.map((e, i) => (<button key={i} type="button" onClick={() => addEmoji(e)}>{e}</button>))}</div>)}
              </>
            ) : (<a href="/login" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '12px', background: 'linear-gradient(135deg, #e50914, #b20710)', borderRadius: '12px', color: 'white', textDecoration: 'none', fontWeight: '500' }}><i className="fas fa-sign-in-alt"></i>Login to chat</a>)}
          </form>
        </aside>
        )}
      </main>

      <style>{styles}</style>
    </div>
  );
}

const styles = `
        /* ===== BASE ===== */
        .streamer-page {
          height: 100vh;
          height: 100dvh;
          max-height: 100vh;
          max-height: 100dvh;
          display: flex;
          flex-direction: column;
          background: linear-gradient(135deg, #0a0a15 0%, #12121f 50%, #0a0a15 100%);
          color: #fff;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          overflow: hidden;
        }

        .streamer-page.fullscreen {
          position: fixed;
          inset: 0;
          z-index: 9999;
        }

        /* ===== NOTIFICATIONS ===== */
        .notifications-container {
          position: fixed;
          top: 80px;
          right: 20px;
          z-index: 10000;
          display: flex;
          flex-direction: column;
          gap: 8px;
          max-width: 280px;
        }

        .notification {
          padding: 10px 16px;
          background: rgba(30, 30, 50, 0.95);
          backdrop-filter: blur(20px);
          border-radius: 10px;
          border-left: 3px solid #e50914;
          box-shadow: 0 8px 30px rgba(0,0,0,0.3);
          animation: slideIn 0.3s ease, fadeOut 0.3s ease 4.7s forwards;
          font-size: 0.85rem;
        }

        .notification.success { border-left-color: #00d26a; }
        .notification.error { border-left-color: #ff4757; }
        .notification.gift { border-left-color: #ffd700; background: linear-gradient(135deg, rgba(255,215,0,0.15), rgba(30,30,50,0.95)); }

        @keyframes slideIn {
          from { opacity: 0; transform: translateX(100px); }
          to { opacity: 1; transform: translateX(0); }
        }

        @keyframes fadeOut {
          to { opacity: 0; transform: translateX(100px); }
        }

        /* ===== TOP NAV ===== */
        .top-nav {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 8px 16px;
          background: #000000;
          border-bottom: 1px solid rgba(255,255,255,0.1);
          flex-shrink: 0;
          height: 56px;
          min-height: 56px;
          z-index: 100;
        }

        .nav-left, .nav-right {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .logo {
          display: flex;
          align-items: center;
          gap: 8px;
          text-decoration: none;
          color: white;
          font-weight: 800;
        }

        .logo img {
          height: 40px;
        }

        .logo-text {
          display: flex;
          flex-direction: column;
          justify-content: center;
        }

        .logo-title {
          font-weight: 900;
          font-size: 1.125rem;
          letter-spacing: -0.5px;
          line-height: 1.1;
        }

        .logo-title .text-white {
          color: white;
        }

        .logo-title .text-red {
          color: #e50914;
        }

        .logo-subtitle {
          font-size: 0.5rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 1.2px;
          color: white;
          opacity: 0.9;
        }

        @media (min-width: 640px) {
          .logo img {
            height: 48px;
          }
          .logo-title {
            font-size: 1.25rem;
          }
          .logo-subtitle {
            font-size: 0.625rem;
          }
        }

        @media (min-width: 1024px) {
          .logo img {
            height: 56px;
          }
          .logo-title {
            font-size: 1.5rem;
          }
          .logo-subtitle {
            font-size: 0.75rem;
          }
        }

        .live-indicator {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 6px 14px;
          background: linear-gradient(135deg, #e50914, #b20710);
          border-radius: 20px;
          font-size: 0.75rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          box-shadow: 0 4px 20px rgba(229, 9, 20, 0.5);
          animation: liveGlow 2s ease-in-out infinite;
        }

        @keyframes liveGlow {
          0%, 100% { box-shadow: 0 4px 20px rgba(229, 9, 20, 0.5); }
          50% { box-shadow: 0 4px 35px rgba(229, 9, 20, 0.8); }
        }

        .pulse-dot {
          width: 8px;
          height: 8px;
          background: white;
          border-radius: 50%;
          animation: pulse 1.5s ease-in-out infinite;
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.4; transform: scale(0.7); }
        }

        .duration {
          font-variant-numeric: tabular-nums;
          opacity: 0.9;
        }

        .nav-center {
          flex: 1;
          max-width: 550px;
          margin: 0 32px;
        }

        .title-wrapper {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 10px 16px;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 12px;
          transition: all 0.3s ease;
        }

        .title-wrapper:focus-within {
          background: rgba(255,255,255,0.06);
          border-color: rgba(229, 9, 20, 0.5);
          box-shadow: 0 0 25px rgba(229, 9, 20, 0.15);
        }

        .title-wrapper i {
          color: rgba(255,255,255,0.4);
          font-size: 0.85rem;
        }

        .title-wrapper input {
          flex: 1;
          background: transparent;
          border: none;
          color: white;
          font-size: 0.95rem;
          outline: none;
        }

        .title-wrapper input::placeholder {
          color: rgba(255,255,255,0.35);
        }

        .title-wrapper input:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .stat-item {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 14px;
          background: rgba(255,255,255,0.04);
          border-radius: 20px;
          font-size: 0.85rem;
          font-weight: 600;
        }

        .stat-item i {
          color: #e50914;
        }

        .stat-item.peak i {
          color: #00d26a;
        }

        .nav-btn {
          width: 44px;
          height: 44px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 12px;
          color: rgba(255,255,255,0.7);
          font-size: 1rem;
          cursor: pointer;
          transition: all 0.2s ease;
          position: relative;
        }

        .nav-btn:hover {
          background: rgba(255,255,255,0.08);
          color: white;
          transform: translateY(-2px);
        }

        .nav-btn.chat-btn.has-unread {
          border-color: #e50914;
        }

        .nav-btn .badge {
          position: absolute;
          top: -5px;
          right: -5px;
          min-width: 20px;
          height: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #e50914;
          border-radius: 10px;
          font-size: 0.65rem;
          font-weight: 700;
        }

        /* ===== MAIN CONTENT ===== */
        .main-content {
          flex: 1;
          display: flex;
          min-height: 0;
          height: 0;
          overflow: hidden;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .main-content.with-chat .video-section {
          flex: 0 0 75%;
        }

        /* Mobile Layout - Video on top, Chat below (always visible like YouTube) */
        /* Uses Visual Viewport API for bulletproof iOS keyboard handling */
        .main-content.mobile-layout {
          position: fixed;
          top: 56px;
          left: 0;
          right: 0;
          bottom: 0;
          height: calc(var(--visible-height, 100vh) - 56px);
          flex-direction: column;
          display: flex;
          overflow: hidden;
          transform: translateZ(0);
          -webkit-transform: translateZ(0);
        }

        /* Video section - FIXED height, NEVER moves */
        .main-content.mobile-layout .video-section {
          flex: 0 0 180px !important;
          height: 180px !important;
          min-height: 180px !important;
          max-height: 180px !important;
          display: flex;
          flex-direction: column;
          position: relative;
          background: #000;
          transform: translateZ(0);
          -webkit-transform: translateZ(0);
        }

        .main-content.mobile-layout .video-container {
          flex: 1;
          position: relative;
          overflow: hidden;
        }

        .main-content.mobile-layout .preview-video {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }
        
        .main-content.mobile-layout .camera-off {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #0d0d1a 0%, #1a1a2e 100%);
          gap: 8px;
        }
        
        .main-content.mobile-layout .camera-off .camera-icon {
          width: 50px;
          height: 50px;
          font-size: 1.3rem;
          background: rgba(229, 9, 20, 0.1);
          border: 1px solid rgba(229, 9, 20, 0.3);
          color: rgba(229, 9, 20, 0.8);
        }
        
        .main-content.mobile-layout .camera-off p {
          font-size: 0.8rem;
          color: rgba(255,255,255,0.5);
          margin: 0;
        }
        
        .main-content.mobile-layout .camera-off .streamer-badge {
          margin-top: 6px;
          padding: 6px 12px;
          gap: 6px;
        }
        
        .main-content.mobile-layout .camera-off .streamer-badge img {
          width: 22px;
          height: 22px;
        }
        
        .main-content.mobile-layout .camera-off .streamer-badge span {
          font-size: 0.75rem;
        }

        /* Chat section - FLEX-GROW to fill remaining space */
        .main-content.mobile-layout .chat-section.mobile {
          position: relative !important;
          transform: none !important;
          flex: 1 1 auto;
          min-height: 0;
          max-height: none;
          border-radius: 0;
          box-shadow: none;
          border-top: 1px solid rgba(255,255,255,0.1);
          display: flex !important;
          flex-direction: column;
          overflow: hidden;
          background: rgba(18, 18, 30, 0.98);
        }
        
        .main-content.mobile-layout .chat-section.mobile .chat-header {
          flex: 0 0 auto;
          padding: 10px 12px;
        }
        
        /* Chat messages - scrollable, flex-grow */
        .main-content.mobile-layout .chat-section.mobile .chat-messages {
          flex: 1 1 auto;
          min-height: 0;
          overflow-y: auto;
          -webkit-overflow-scrolling: touch;
          overscroll-behavior: contain;
        }
        
        .main-content.mobile-layout .chat-section.mobile .reactions-bar {
          flex: 0 0 auto;
        }
        
        /* Input - stays at bottom, sits above keyboard */
        .main-content.mobile-layout .chat-section.mobile .chat-form {
          flex: 0 0 auto;
          padding: 8px 12px;
          padding-bottom: 8px;
          background: #1a1a2e;
          flex-shrink: 0;
        }

        /* When keyboard is open - hide non-essential UI to save space */
        .main-content.mobile-layout.keyboard-open .chat-header {
          display: none !important;
        }
        
        .main-content.mobile-layout.keyboard-open .reactions-bar {
          display: none !important;
        }
        
        .main-content.mobile-layout.keyboard-open .chat-form {
          padding: 6px 10px !important;
          padding-bottom: 6px !important;
          background: #1a1a2e !important;
        }
        
        /* Ensure chat-form never gets cut off */
        .main-content.mobile-layout.keyboard-open .chat-section.mobile {
          overflow: visible;
        }

        /* Mobile video controls - fixed at bottom of video */
        .main-content.mobile-layout .video-controls {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          background: linear-gradient(transparent, rgba(0,0,0,0.9));
          padding: 12px 16px;
          z-index: 10;
        }

        .video-section {
          flex: 1;
          display: flex;
          flex-direction: column;
          min-width: 0;
          min-height: 0;
          overflow: hidden;
        }

        .video-container {
          flex: 1;
          position: relative;
          background: #000;
          overflow: hidden;
          min-height: 0;
        }

        /* ===== CUSTOM FULLSCREEN PLAYER - iOS Native ===== */
        .video-container.custom-fullscreen {
          position: fixed !important;
          top: 0 !important;
          left: 0 !important;
          right: 0 !important;
          bottom: 0 !important;
          width: 100vw !important;
          width: 100dvw !important;
          height: 100vh !important;
          height: 100dvh !important;
          z-index: 99999 !important;
          background: #000 !important;
          margin: 0 !important;
          padding: 0 !important;
        }

        .video-container.custom-fullscreen .preview-video {
          position: absolute !important;
          top: 0 !important;
          left: 0 !important;
          width: 100% !important;
          height: 100% !important;
          object-fit: contain !important;
        }

        /* iOS Portrait - rotate to landscape */
        @media screen and (max-width: 932px) and (orientation: portrait) {
          .video-container.custom-fullscreen {
            width: 100vh !important;
            height: 100vw !important;
            transform: rotate(90deg);
            transform-origin: top left;
            left: 100vw !important;
            top: 0 !important;
          }
        }

        /* Landscape - normal */
        @media screen and (orientation: landscape) {
          .video-container.custom-fullscreen {
            transform: none !important;
            left: 0 !important;
          }
        }

        .video-container.custom-fullscreen .video-controls {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          z-index: 100000;
          padding: 16px 20px;
          padding-bottom: calc(16px + env(safe-area-inset-bottom, 0px));
          background: linear-gradient(transparent, rgba(0,0,0,0.9));
        }

        .video-container.custom-fullscreen .fullscreen-chat-overlay {
          z-index: 100000;
          bottom: 100px;
        }

        .video-container.custom-fullscreen .pause-overlay {
          z-index: 99998;
        }

        /* Fullscreen Exit Button */
        .fullscreen-exit-btn {
          position: absolute;
          top: env(safe-area-inset-top, 20px);
          right: 20px;
          width: 50px;
          height: 50px;
          border-radius: 50%;
          background: rgba(0, 0, 0, 0.7);
          border: 2px solid rgba(255, 255, 255, 0.3);
          color: white;
          font-size: 1.3rem;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          z-index: 100001;
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
          transition: all 0.2s ease;
        }

        .fullscreen-exit-btn:hover,
        .fullscreen-exit-btn:active {
          background: rgba(229, 9, 20, 0.8);
          border-color: #e50914;
          transform: scale(1.1);
        }

        /* Fullscreen Mute Button */
        .fullscreen-mute-btn {
          position: absolute;
          top: env(safe-area-inset-top, 20px);
          left: 20px;
          width: 50px;
          height: 50px;
          border-radius: 50%;
          background: rgba(0, 0, 0, 0.7);
          border: 2px solid rgba(255, 255, 255, 0.3);
          color: white;
          font-size: 1.3rem;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          z-index: 100001;
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
          transition: all 0.2s ease;
        }

        .fullscreen-mute-btn:hover,
        .fullscreen-mute-btn:active {
          background: rgba(229, 9, 20, 0.8);
          border-color: #e50914;
          transform: scale(1.1);
        }

        .fullscreen-mute-btn.muted {
          color: #e50914;
          border-color: #e50914;
        }

        /* Hide nav and chat when fullscreen */
        body.fullscreen-active .top-nav {
          display: none !important;
        }
        
        body.fullscreen-active .chat-section {
          display: none !important;
        }
        
        body.fullscreen-active .stream-info {
          display: none !important;
        }
        
        body.fullscreen-active {
          overflow: hidden !important;
        }

        .preview-video {
          width: 100%;
          height: 100%;
          object-fit: contain;
          transition: opacity 0.3s;
        }
        
        /* Hide iOS native video controls and play button */
        .preview-video::-webkit-media-controls {
          display: none !important;
        }
        .preview-video::-webkit-media-controls-enclosure {
          display: none !important;
        }
        .preview-video::-webkit-media-controls-panel {
          display: none !important;
        }
        .preview-video::-webkit-media-controls-start-playback-button {
          display: none !important;
          -webkit-appearance: none;
        }

        .preview-video.hidden {
          opacity: 0;
        }

        .pause-overlay {
          position: absolute;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(0,0,0,0.4);
          z-index: 5;
          pointer-events: none;
        }

        .pause-overlay i {
          font-size: 4rem;
          color: white;
          opacity: 0.9;
          text-shadow: 0 2px 10px rgba(0,0,0,0.5);
        }

        /* Fullscreen Chat Overlay - Twitch/YouTube style */
        .fullscreen-chat-overlay {
          position: absolute;
          bottom: 80px;
          left: 20px;
          max-width: 400px;
          display: flex;
          flex-direction: column;
          gap: 8px;
          z-index: 100;
          pointer-events: none;
        }

        .overlay-message {
          display: flex;
          gap: 8px;
          padding: 8px 14px;
          background: rgba(0, 0, 0, 0.7);
          backdrop-filter: blur(8px);
          border-radius: 20px;
          animation: messageSlideIn 0.3s ease-out, messageFadeOut 0.5s ease-in 4.5s forwards;
          max-width: 100%;
        }

        .overlay-username {
          font-weight: 700;
          color: #e50914;
          font-size: 0.9rem;
          white-space: nowrap;
        }

        .overlay-text {
          color: white;
          font-size: 0.9rem;
          word-break: break-word;
        }

        @keyframes messageSlideIn {
          from {
            opacity: 0;
            transform: translateX(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes messageFadeOut {
          from {
            opacity: 1;
          }
          to {
            opacity: 0;
          }
        }

        .camera-off {
          position: absolute;
          inset: 0;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #15152a, #0a0a15);
          gap: 16px;
        }

        .camera-icon {
          width: 120px;
          height: 120px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255,255,255,0.03);
          border: 2px solid rgba(255,255,255,0.08);
          border-radius: 50%;
          font-size: 3rem;
          color: rgba(255,255,255,0.2);
        }

        .camera-off p {
          color: rgba(255,255,255,0.4);
          font-size: 1.1rem;
        }
        
        .streamer-badge {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-top: 16px;
          padding: 10px 20px;
          background: rgba(255,255,255,0.05);
          border-radius: 30px;
          border: 1px solid rgba(255,255,255,0.08);
        }
        
        .streamer-badge img {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          object-fit: cover;
        }
        
        .streamer-badge span {
          color: rgba(255,255,255,0.8);
          font-size: 0.9rem;
          font-weight: 500;
        }

        .thumbnail-preview {
          position: absolute;
          bottom: 80px;
          right: 20px;
          width: 200px;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 10px 40px rgba(0,0,0,0.5);
        }

        .thumbnail-preview img {
          width: 100%;
          display: block;
        }

        .thumbnail-preview span {
          display: block;
          padding: 8px;
          background: rgba(0,0,0,0.8);
          font-size: 0.75rem;
          text-align: center;
        }

        /* ===== LIVE OVERLAY ===== */
        .live-overlay {
          position: absolute;
          top: 16px;
          left: 16px;
          right: 16px;
          display: flex;
          justify-content: space-between;
          pointer-events: none;
        }

        .overlay-left, .overlay-right {
          display: flex;
          gap: 8px;
        }

        .live-overlay .stat {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 14px;
          background: rgba(0,0,0,0.75);
          backdrop-filter: blur(12px);
          border-radius: 8px;
          font-size: 0.8rem;
          font-weight: 600;
        }

        .live-overlay .stat.live-badge {
          background: rgba(229, 9, 20, 0.95);
        }

        .live-overlay .stat.screen-share {
          background: rgba(229, 9, 20, 0.9);
        }

        .live-overlay .stat .dot {
          width: 6px;
          height: 6px;
          background: white;
          border-radius: 50%;
          animation: pulse 1.5s infinite;
        }

        /* ===== VIDEO CONTROLS ===== */
        .video-controls {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 16px;
          background: linear-gradient(transparent, rgba(0,0,0,0.95));
          flex-shrink: 0;
          z-index: 250;
        }

        .controls-left, .controls-center, .controls-right {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .ctrl-btn {
          width: 44px;
          height: 44px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255,255,255,0.08);
          backdrop-filter: blur(12px);
          border: 1px solid rgba(255,255,255,0.12);
          border-radius: 50%;
          color: white;
          font-size: 1rem;
          cursor: pointer;
          transition: all 0.25s ease;
        }

        .ctrl-btn.small {
          width: 36px;
          height: 36px;
          font-size: 0.85rem;
        }

        .ctrl-btn:hover {
          background: rgba(255,255,255,0.15);
          transform: scale(1.08);
        }

        .ctrl-btn.off {
          background: rgba(229, 9, 20, 0.85);
          border-color: rgba(229, 9, 20, 0.5);
        }

        .ctrl-btn.active {
          background: rgba(229, 9, 20, 0.85);
          border-color: rgba(229, 9, 20, 0.5);
        }

        .ctrl-btn.has-unread {
          border: 2px solid #e50914;
        }

        .ctrl-btn .mobile-badge {
          position: absolute;
          top: -4px;
          right: -4px;
          min-width: 18px;
          height: 18px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #e50914;
          border-radius: 9px;
          font-size: 0.6rem;
          font-weight: 700;
        }

        .volume-control {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 6px 14px 6px 6px;
          background: rgba(0,0,0,0.5);
          border-radius: 26px;
        }

        .volume-slider {
          width: 100px;
          height: 4px;
          -webkit-appearance: none;
          background: rgba(255,255,255,0.2);
          border-radius: 2px;
          cursor: pointer;
        }

        .volume-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          width: 14px;
          height: 14px;
          background: white;
          border-radius: 50%;
          box-shadow: 0 2px 8px rgba(0,0,0,0.3);
          transition: transform 0.2s;
        }

        .volume-slider::-webkit-slider-thumb:hover {
          transform: scale(1.2);
        }

        .go-live-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 10px 20px;
          background: linear-gradient(135deg, #e50914, #b20710);
          border: none;
          border-radius: 24px;
          color: white;
          font-size: 0.85rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 4px 20px rgba(229, 9, 20, 0.4);
          white-space: nowrap;
        }

        .go-live-btn:hover:not(:disabled) {
          transform: scale(1.03) translateY(-1px);
          box-shadow: 0 6px 25px rgba(229, 9, 20, 0.5);
        }

        .go-live-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .spinner {
          width: 40px;
          height: 40px;
          border: 3px solid rgba(255,255,255,0.25);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
          aspect-ratio: 1 / 1;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .end-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 10px 20px;
          background: linear-gradient(135deg, #e50914, #b20710);
          border: none;
          border-radius: 24px;
          color: white;
          font-size: 0.85rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 4px 20px rgba(229, 9, 20, 0.4);
          white-space: nowrap;
        }

        .end-btn:hover {
          transform: scale(1.03);
          box-shadow: 0 6px 25px rgba(229, 9, 20, 0.5);
        }

        .more-menu-wrapper {
          position: relative;
        }

        .more-menu {
          position: absolute;
          bottom: 100%;
          right: 0;
          margin-bottom: 10px;
          min-width: 180px;
          background: rgba(25, 25, 40, 0.98);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 15px 50px rgba(0,0,0,0.5);
          animation: menuSlide 0.2s ease;
        }

        @keyframes menuSlide {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .more-menu button {
          display: flex;
          align-items: center;
          gap: 12px;
          width: 100%;
          padding: 14px 18px;
          background: transparent;
          border: none;
          color: rgba(255,255,255,0.85);
          font-size: 0.9rem;
          cursor: pointer;
          transition: background 0.2s;
          text-align: left;
        }

        .more-menu button:hover {
          background: rgba(255,255,255,0.08);
        }

        .more-menu button i {
          width: 18px;
          color: #e50914;
        }

        /* ===== ERROR BANNER ===== */
        .error-banner {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 14px 20px;
          background: linear-gradient(135deg, rgba(229, 9, 20, 0.95), rgba(180, 0, 0, 0.95));
          color: white;
          font-size: 0.9rem;
        }

        .error-banner button {
          margin-left: auto;
          background: none;
          border: none;
          color: rgba(255,255,255,0.7);
          cursor: pointer;
          padding: 4px;
        }

        .error-banner button:hover {
          color: white;
        }

        /* ===== STREAM INFO BAR ===== */
        .stream-info {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 10px 16px;
          background: rgba(15, 15, 25, 0.98);
          border-top: 1px solid rgba(255,255,255,0.05);
          flex-shrink: 0;
          position: relative;
          z-index: 250;
        }

        .streamer-info {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .streamer-info .avatar {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          border: 2px solid #e50914;
          box-shadow: 0 0 15px rgba(229, 9, 20, 0.35);
        }

        .info-text h3 {
          font-size: 0.9rem;
          font-weight: 700;
          margin: 0 0 2px 0;
        }

        .info-text .status {
          font-size: 0.75rem;
          color: rgba(255,255,255,0.5);
        }

        .info-text .status.live {
          color: #e50914;
        }

        .stream-actions {
          display: flex;
          gap: 8px;
        }

        .action-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 14px;
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 8px;
          color: rgba(255,255,255,0.8);
          font-size: 0.8rem;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .action-btn:hover {
          background: rgba(255,255,255,0.1);
          color: white;
        }

        /* ===== CHAT SECTION ===== */
        .chat-section {
          width: 25%;
          min-width: 320px;
          max-width: 400px;
          height: 100%;
          display: flex;
          flex-direction: column;
          background: rgba(18, 18, 30, 0.98);
          border-left: 1px solid rgba(255,255,255,0.05);
          transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
          overflow: hidden;
        }

        .chat-section.closed {
          width: 0;
          min-width: 0;
          overflow: hidden;
          border: none;
        }

        .chat-section.floating {
          position: fixed;
          top: 70px;
          right: 20px;
          bottom: 120px;
          width: 380px;
          max-width: 90vw;
          max-height: calc(100vh - 200px);
          border-radius: 16px;
          border: 1px solid rgba(255,255,255,0.1);
          box-shadow: 0 25px 80px rgba(0,0,0,0.5);
          z-index: 200;
          display: flex;
          flex-direction: column;
        }

        .chat-section.floating .chat-messages {
          flex: 1;
          min-height: 0;
          max-height: calc(100vh - 420px);
        }

        .chat-section.mobile {
          /* Mobile uses flex layout, not fixed positioning */
          position: relative;
          width: 100%;
          max-width: 100%;
          z-index: 50;
        }

        .chat-section.mobile.open {
          /* No transform needed for flex layout */
        }

        .chat-drag-handle {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 10px;
          cursor: pointer;
        }

        .chat-drag-handle .handle-bar {
          width: 40px;
          height: 4px;
          background: rgba(255,255,255,0.3);
          border-radius: 2px;
        }

        .chat-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 14px 18px;
          background: rgba(229, 9, 20, 0.08);
          border-bottom: 1px solid rgba(255,255,255,0.05);
        }

        .chat-tabs {
          display: flex;
          gap: 4px;
        }

        .chat-tabs .tab {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 14px;
          background: transparent;
          border: none;
          border-radius: 8px;
          color: rgba(255,255,255,0.5);
          font-size: 0.85rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .chat-tabs .tab.active {
          background: rgba(229, 9, 20, 0.15);
          color: #e50914;
        }

        .chat-tabs .tab:hover:not(.active) {
          color: rgba(255,255,255,0.8);
        }

        .chat-actions {
          display: flex;
          gap: 6px;
        }

        .small-btn {
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255,255,255,0.05);
          border: none;
          border-radius: 8px;
          color: rgba(255,255,255,0.5);
          font-size: 0.8rem;
          cursor: pointer;
          transition: all 0.2s;
        }

        .small-btn:hover {
          background: rgba(255,255,255,0.1);
          color: white;
        }

        .small-btn.active {
          background: rgba(229, 9, 20, 0.2);
          color: #e50914;
        }

        .small-btn.close:hover {
          background: rgba(229, 9, 20, 0.2);
          color: #e50914;
        }

        .chat-messages {
          flex: 1;
          overflow-y: auto;
          padding: 16px;
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .chat-messages::-webkit-scrollbar {
          width: 6px;
        }

        .chat-messages::-webkit-scrollbar-track {
          background: transparent;
        }

        .chat-messages::-webkit-scrollbar-thumb {
          background: rgba(255,255,255,0.1);
          border-radius: 3px;
        }

        .chat-empty {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          color: rgba(255,255,255,0.25);
          gap: 8px;
        }

        .chat-empty i {
          font-size: 3.5rem;
          opacity: 0.4;
          margin-bottom: 8px;
        }

        .chat-empty p {
          font-size: 1.05rem;
          margin: 0;
        }

        .chat-empty span {
          font-size: 0.85rem;
        }

        .message {
          position: relative;
          padding: 12px 14px;
          background: rgba(255,255,255,0.03);
          border-radius: 12px;
          animation: msgIn 0.3s ease;
        }

        @keyframes msgIn {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .message.streamer {
          background: linear-gradient(135deg, rgba(229, 9, 20, 0.12), rgba(229, 9, 20, 0.05));
          border: 1px solid rgba(229, 9, 20, 0.2);
        }

        .message.highlighted {
          background: linear-gradient(135deg, rgba(255, 215, 0, 0.15), rgba(255, 215, 0, 0.05));
          border: 1px solid rgba(255, 215, 0, 0.3);
        }

        .msg-header {
          display: flex;
          align-items: center;
          gap: 6px;
          margin-bottom: 6px;
        }

        .msg-badges {
          display: flex;
          gap: 4px;
        }

        .badge-icon {
          font-size: 0.8rem;
        }

        .msg-author {
          font-weight: 700;
          color: #e50914;
          font-size: 0.9rem;
        }

        .msg-author.highlighted-user {
          color: #00d26a;
          text-shadow: 0 0 10px rgba(0, 210, 106, 0.3);
        }

        .message.streamer .msg-author {
          color: #ff6b9d;
        }

        .message.user-highlighted {
          background: linear-gradient(135deg, rgba(0, 210, 106, 0.15), rgba(0, 210, 106, 0.05));
          border-left: 3px solid #00d26a;
        }

        .badge-icon.highlight {
          animation: sparkle 1.5s ease-in-out infinite;
        }

        @keyframes sparkle {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }

        .msg-time {
          font-size: 0.75rem;
          color: rgba(255,255,255,0.3);
          margin-left: auto;
        }

        .msg-content {
          font-size: 0.9rem;
          line-height: 1.5;
          color: rgba(255,255,255,0.9);
          word-break: break-word;
        }

        .msg-actions {
          position: absolute;
          top: 8px;
          right: 8px;
          display: none;
          gap: 4px;
        }

        .message:hover .msg-actions {
          display: flex;
        }

        .msg-actions button {
          width: 26px;
          height: 26px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(0,0,0,0.5);
          border: none;
          border-radius: 6px;
          color: rgba(255,255,255,0.6);
          font-size: 0.7rem;
          cursor: pointer;
          transition: all 0.2s;
        }

        .msg-actions button:hover {
          background: rgba(255,255,255,0.1);
          color: white;
        }

        /* ===== REACTIONS BAR ===== */
        .reactions-bar {
          display: flex;
          justify-content: center;
          gap: 6px;
          padding: 10px;
          border-top: 1px solid rgba(255,255,255,0.05);
        }

        .reaction-btn {
          width: 38px;
          height: 38px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255,255,255,0.04);
          border: none;
          border-radius: 10px;
          font-size: 1.2rem;
          cursor: pointer;
          transition: all 0.2s;
        }

        .reaction-btn:hover {
          background: rgba(255,255,255,0.1);
          transform: scale(1.15);
        }

        /* ===== CHAT INPUT ===== */
        .chat-form {
          padding: 14px;
          background: rgba(0,0,0,0.3);
          border-top: 1px solid rgba(255,255,255,0.05);
        }

        .input-wrapper {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 6px;
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 14px;
          transition: all 0.2s;
        }

        .input-wrapper:focus-within {
          border-color: rgba(229, 9, 20, 0.5);
        }

        .emoji-btn {
          width: 38px;
          height: 38px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: transparent;
          border: none;
          border-radius: 10px;
          color: rgba(255,255,255,0.5);
          font-size: 1.1rem;
          cursor: pointer;
          transition: all 0.2s;
        }

        .emoji-btn:hover {
          background: rgba(255,255,255,0.08);
          color: white;
        }

        .input-wrapper input {
          flex: 1;
          background: transparent;
          border: none;
          color: white;
          font-size: 0.95rem;
          outline: none;
        }

        .input-wrapper input::placeholder {
          color: rgba(255,255,255,0.35);
        }

        .send-btn {
          width: 42px;
          height: 42px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #e50914, #b20710);
          border: none;
          border-radius: 12px;
          color: white;
          font-size: 1rem;
          cursor: pointer;
          transition: all 0.2s;
        }

        .send-btn:hover:not(:disabled) {
          transform: scale(1.05);
          box-shadow: 0 4px 18px rgba(229, 9, 20, 0.4);
        }

        .send-btn:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }

        .emoji-picker {
          display: grid;
          grid-template-columns: repeat(8, 1fr);
          gap: 6px;
          padding: 12px;
          margin-top: 10px;
          background: rgba(0,0,0,0.4);
          border-radius: 12px;
          animation: fadeIn 0.2s ease;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .emoji-picker button {
          width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255,255,255,0.05);
          border: none;
          border-radius: 8px;
          font-size: 1.2rem;
          cursor: pointer;
          transition: all 0.2s;
        }

        .emoji-picker button:hover {
          background: rgba(255,255,255,0.12);
          transform: scale(1.1);
        }

        /* ===== MODALS ===== */
        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.8);
          backdrop-filter: blur(8px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
          animation: fadeIn 0.2s ease;
        }

        .modal {
          width: 100%;
          max-width: 500px;
          max-height: 90vh;
          overflow-y: auto;
          background: linear-gradient(135deg, #1a1a2e, #15152a);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 20px;
          box-shadow: 0 30px 100px rgba(0,0,0,0.6);
          animation: modalIn 0.3s ease;
        }

        @keyframes modalIn {
          from { opacity: 0; transform: scale(0.95) translateY(20px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }

        .modal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 20px 24px;
          background: rgba(229, 9, 20, 0.08);
          border-bottom: 1px solid rgba(255,255,255,0.05);
        }

        .modal-header h2 {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 1.2rem;
          margin: 0;
        }

        .modal-header h2 i {
          color: #e50914;
        }

        .modal-header button {
          width: 38px;
          height: 38px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255,255,255,0.05);
          border: none;
          border-radius: 10px;
          color: rgba(255,255,255,0.6);
          font-size: 1rem;
          cursor: pointer;
          transition: all 0.2s;
        }

        .modal-header button:hover {
          background: rgba(255,255,255,0.1);
          color: white;
        }

        .modal-content {
          padding: 24px;
        }

        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          padding: 20px 24px;
          background: rgba(0,0,0,0.2);
          border-top: 1px solid rgba(255,255,255,0.05);
        }

        .setting-group {
          margin-bottom: 24px;
        }

        .setting-group label {
          display: block;
          margin-bottom: 10px;
          font-size: 0.95rem;
          font-weight: 600;
          color: rgba(255,255,255,0.9);
        }

        .setting-group small {
          display: block;
          margin-top: 8px;
          font-size: 0.8rem;
          color: rgba(255,255,255,0.4);
        }

        .setting-group input[type="text"],
        .setting-group input[type="password"],
        .setting-group select {
          width: 100%;
          padding: 14px 16px;
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 12px;
          color: white;
          font-size: 0.95rem;
          outline: none;
          transition: all 0.2s;
        }

        .setting-group input:focus,
        .setting-group select:focus {
          border-color: rgba(229, 9, 20, 0.5);
        }

        .setting-group select option {
          background: #1a1a2e;
        }

        .key-input {
          display: flex;
          gap: 8px;
        }

        .key-input input {
          flex: 1;
        }

        .key-input button {
          width: 48px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255,255,255,0.08);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 12px;
          color: rgba(255,255,255,0.7);
          cursor: pointer;
          transition: all 0.2s;
        }

        .key-input button:hover {
          background: rgba(229, 9, 20, 0.2);
          color: #e50914;
        }

        .settings-row {
          display: flex;
          gap: 16px;
        }

        .setting-group.half {
          flex: 1;
        }

        .thumbnail-upload {
          display: flex;
          justify-content: center;
        }

        .upload-btn {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 12px;
          padding: 30px 50px;
          background: rgba(255,255,255,0.04);
          border: 2px dashed rgba(255,255,255,0.15);
          border-radius: 16px;
          color: rgba(255,255,255,0.5);
          cursor: pointer;
          transition: all 0.3s;
        }

        .upload-btn:hover {
          background: rgba(229, 9, 20, 0.08);
          border-color: rgba(229, 9, 20, 0.4);
          color: #e50914;
        }

        .upload-btn i {
          font-size: 2.5rem;
        }

        .thumb-preview {
          position: relative;
          width: 200px;
          border-radius: 12px;
          overflow: hidden;
        }

        .thumb-preview img {
          width: 100%;
          display: block;
        }

        .thumb-preview button {
          position: absolute;
          top: 8px;
          right: 8px;
          width: 28px;
          height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(0,0,0,0.8);
          border: none;
          border-radius: 50%;
          color: white;
          cursor: pointer;
        }

        .range-labels {
          display: flex;
          justify-content: space-between;
          margin-top: 10px;
          font-size: 0.8rem;
          color: rgba(255,255,255,0.4);
        }

        .range-labels .current {
          color: #e50914;
          font-weight: 600;
        }

        /* Source Toggle */
        .source-toggle {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
        }

        .source-btn {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          padding: 20px 16px;
          background: rgba(255,255,255,0.05);
          border: 2px solid rgba(255,255,255,0.1);
          border-radius: 16px;
          color: rgba(255,255,255,0.7);
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .source-btn:hover {
          background: rgba(255,255,255,0.08);
          border-color: rgba(255,255,255,0.2);
          color: white;
        }

        .source-btn.active {
          background: rgba(229, 9, 20, 0.15);
          border-color: #e50914;
          color: white;
        }

        .source-btn i {
          font-size: 1.8rem;
        }

        .source-btn span {
          font-weight: 600;
          font-size: 0.95rem;
        }

        .source-btn small {
          font-size: 0.75rem;
          color: rgba(255,255,255,0.5);
        }

        .source-btn.active small {
          color: rgba(229, 9, 20, 0.8);
        }

        /* OBS Instructions */
        .obs-instructions {
          padding: 16px;
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 12px;
          margin-top: 16px;
        }

        .obs-instructions h4 {
          display: flex;
          align-items: center;
          gap: 8px;
          margin: 0 0 12px 0;
          font-size: 0.9rem;
          color: rgba(255,255,255,0.9);
        }

        .obs-instructions h4 i {
          color: #e50914;
        }

        .obs-instructions ol {
          margin: 0;
          padding-left: 20px;
        }

        .obs-instructions li {
          font-size: 0.85rem;
          color: rgba(255,255,255,0.7);
          margin-bottom: 8px;
          line-height: 1.5;
        }

        .obs-instructions code {
          display: inline-block;
          padding: 2px 8px;
          background: rgba(255,255,255,0.1);
          border-radius: 4px;
          font-family: monospace;
          font-size: 0.8rem;
          color: #e50914;
        }

        .warning-text {
          display: block;
          margin-top: 8px;
          padding: 10px;
          background: rgba(255, 193, 7, 0.1);
          border: 1px solid rgba(255, 193, 7, 0.3);
          border-radius: 8px;
          color: #ffc107;
          font-size: 0.8rem;
        }

        .upload-btn small {
          display: block;
          margin-top: 4px;
          font-size: 0.75rem;
          color: rgba(255,255,255,0.4);
        }

        .setting-group input[type="range"] {
          width: 100%;
          height: 6px;
          -webkit-appearance: none;
          background: rgba(255,255,255,0.1);
          border-radius: 3px;
          cursor: pointer;
        }

        .setting-group input[type="range"]::-webkit-slider-thumb {
          -webkit-appearance: none;
          width: 18px;
          height: 18px;
          background: #e50914;
          border-radius: 50%;
          box-shadow: 0 2px 10px rgba(229, 9, 20, 0.4);
        }

        .btn {
          padding: 12px 24px;
          border: none;
          border-radius: 10px;
          font-size: 0.95rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn.primary {
          background: linear-gradient(135deg, #e50914, #b20710);
          color: white;
          box-shadow: 0 4px 15px rgba(229, 9, 20, 0.3);
        }

        .btn.primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 25px rgba(229, 9, 20, 0.4);
        }

        .btn.secondary {
          background: rgba(255,255,255,0.08);
          color: rgba(255,255,255,0.8);
        }

        .btn.secondary:hover {
          background: rgba(255,255,255,0.12);
          color: white;
        }

        /* ===== SETTINGS MODAL SPECIFIC ===== */
        .setting-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px 0;
          border-bottom: 1px solid rgba(255,255,255,0.05);
        }

        .setting-item:last-child {
          border-bottom: none;
        }

        .setting-info span {
          display: block;
          font-size: 0.95rem;
          font-weight: 600;
          margin-bottom: 4px;
        }

        .setting-info small {
          font-size: 0.8rem;
          color: rgba(255,255,255,0.4);
        }

        .toggle {
          position: relative;
          width: 54px;
          height: 30px;
          cursor: pointer;
        }

        .toggle input {
          opacity: 0;
          width: 0;
          height: 0;
        }

        .toggle .slider {
          position: absolute;
          inset: 0;
          background: rgba(255,255,255,0.1);
          border-radius: 15px;
          transition: 0.3s;
        }

        .toggle .slider:before {
          content: "";
          position: absolute;
          width: 24px;
          height: 24px;
          left: 3px;
          bottom: 3px;
          background: white;
          border-radius: 50%;
          transition: 0.3s;
        }

        .toggle input:checked + .slider {
          background: linear-gradient(135deg, #e50914, #b20710);
        }

        .toggle input:checked + .slider:before {
          transform: translateX(24px);
        }

        .shortcuts-section {
          margin-top: 24px;
          padding-top: 20px;
          border-top: 1px solid rgba(255,255,255,0.08);
        }

        .shortcuts-section h4 {
          font-size: 0.95rem;
          margin: 0 0 16px 0;
          color: rgba(255,255,255,0.6);
        }

        .shortcut {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 8px 0;
          font-size: 0.9rem;
          color: rgba(255,255,255,0.7);
        }

        .shortcut kbd {
          min-width: 32px;
          padding: 6px 10px;
          background: rgba(255,255,255,0.08);
          border: 1px solid rgba(255,255,255,0.15);
          border-radius: 6px;
          font-family: inherit;
          font-size: 0.8rem;
          text-align: center;
        }

        /* ===== MOBILE FAB ===== */
        .chat-fab {
          position: fixed;
          bottom: 24px;
          right: 24px;
          width: 64px;
          height: 64px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #e50914, #b20710);
          border: none;
          border-radius: 50%;
          color: white;
          font-size: 1.6rem;
          cursor: pointer;
          box-shadow: 0 8px 35px rgba(229, 9, 20, 0.5);
          z-index: 100;
          transition: all 0.3s;
        }

        .chat-fab:hover {
          transform: scale(1.1);
        }

        .fab-badge {
          position: absolute;
          top: -5px;
          right: -5px;
          min-width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #e50914;
          border-radius: 12px;
          font-size: 0.75rem;
          font-weight: 700;
        }

        /* ============================================ */
        /* RESPONSIVE DESIGN - ALL DEVICES */
        /* ============================================ */

        /* Large Desktop & TV (1920px+) */
        @media (min-width: 1920px) {
          .top-nav {
            padding: 16px 32px;
          }
          
          .logo img {
            height: 42px;
          }
          
          .logo span {
            font-size: 1.3rem;
          }
          
          .stat-item {
            padding: 10px 18px;
            font-size: 0.95rem;
          }
          
          .nav-btn {
            width: 52px;
            height: 52px;
          }
          
          .title-wrapper input {
            font-size: 1.05rem;
          }
          
          .main-content.with-chat .video-section {
            flex: 0 0 78%;
          }
          
          .chat-section {
            width: 22%;
            max-width: 450px;
          }
          
          .ctrl-btn {
            width: 58px;
            height: 58px;
            font-size: 1.25rem;
          }
          
          .go-live-btn, .end-btn {
            padding: 14px 32px;
            font-size: 1.05rem;
          }
          
          .stream-info {
            padding: 20px 28px;
          }
          
          .streamer-info .avatar {
            width: 58px;
            height: 58px;
          }
          
          .info-text h3 {
            font-size: 1.15rem;
          }
        }

        /* Desktop (1200px - 1919px) */
        @media (min-width: 1200px) and (max-width: 1919px) {
          .main-content.with-chat .video-section {
            flex: 0 0 76%;
          }
          
          .chat-section {
            width: 24%;
            max-width: 420px;
          }
        }

        /* Laptop (992px - 1199px) */
        @media (min-width: 992px) and (max-width: 1199px) {
          .top-nav {
            padding: 10px 20px;
          }
          
          .nav-center {
            margin: 0 20px;
          }
          
          .main-content.with-chat .video-section {
            flex: 0 0 72%;
          }
          
          .chat-section {
            width: 28%;
            min-width: 280px;
            max-width: 360px;
          }
        }

        /* Tablet Landscape (768px - 991px) */
        @media (min-width: 768px) and (max-width: 991px) {
          .top-nav {
            padding: 10px 16px;
          }
          
          .logo img {
            height: 30px;
          }
          
          .logo span {
            font-size: 0.95rem;
          }
          
          .nav-center {
            margin: 0 16px;
            max-width: 350px;
          }
          
          .title-wrapper {
            padding: 8px 12px;
          }
          
          .title-wrapper input {
            font-size: 0.85rem;
          }
          
          .stat-item {
            padding: 6px 10px;
            font-size: 0.8rem;
          }
          
          .nav-btn {
            width: 40px;
            height: 40px;
          }
          
          .main-content.with-chat .video-section {
            flex: 0 0 65%;
          }
          
          .chat-section {
            width: 35%;
            min-width: 260px;
            max-width: 320px;
          }
          
          .ctrl-btn {
            width: 48px;
            height: 48px;
            font-size: 1.05rem;
          }
          
          .go-live-btn, .end-btn {
            padding: 10px 22px;
            font-size: 0.9rem;
          }
          
          .stream-info {
            padding: 12px 16px;
          }
          
          .streamer-info .avatar {
            width: 44px;
            height: 44px;
          }
          
          .action-btn {
            padding: 8px 14px;
            font-size: 0.85rem;
          }
        }

        /* Tablet Portrait & Large Phones (576px - 767px) */
        @media (min-width: 576px) and (max-width: 767px) {
          .top-nav {
            padding: 8px 12px;
          }

          .logo img {
            height: 28px;
          }
          
          .logo span {
            font-size: 0.85rem;
          }

          .nav-center {
            display: none;
          }
          
          .stat-item {
            padding: 5px 8px;
            font-size: 0.75rem;
          }
          
          .nav-btn {
            width: 38px;
            height: 38px;
            font-size: 0.9rem;
          }

          .main-content.with-chat .video-section {
            flex: 1;
          }

          .video-controls {
            padding: 12px 14px;
          }

          .ctrl-btn {
            width: 44px;
            height: 44px;
            font-size: 0.95rem;
          }

          .go-live-btn, .end-btn {
            padding: 10px 20px;
            font-size: 0.9rem;
          }

          .volume-control {
            display: none;
          }

          .stream-info {
            padding: 10px 12px;
          }
          
          .streamer-info .avatar {
            width: 40px;
            height: 40px;
          }
          
          .info-text h3 {
            font-size: 0.95rem;
          }
          
          .info-text .status {
            font-size: 0.8rem;
          }

          .action-btn {
            padding: 8px 12px;
            font-size: 0.85rem;
          }
          
          .action-btn span {
            display: none;
          }
          
          .chat-section {
            width: 85vw;
            max-width: 340px;
          }
          
          .chat-section.mobile {
            height: 45vh;
            max-height: 380px;
            border-radius: 20px 20px 0 0;
          }

          .modal {
            max-width: 95%;
            margin: 10px;
            border-radius: 16px;
            max-height: 90vh;
          }
          
          .chat-fab {
            width: 56px;
            height: 56px;
            font-size: 1.4rem;
            bottom: 20px;
            right: 20px;
          }
        }

        /* Mobile Phones (320px - 575px) */
        @media (max-width: 575px) {
          .top-nav {
            padding: 8px 10px;
          }

          .logo img {
            height: 26px;
          }
          
          .logo-title {
            font-size: 0.85rem;
          }
          
          .logo-subtitle {
            font-size: 0.4rem;
          }

          .nav-center {
            display: none;
          }
          
          .live-indicator {
            padding: 4px 10px;
            font-size: 0.65rem;
          }
          
          .live-indicator .duration {
            display: none;
          }
          
          .stat-item {
            padding: 4px 8px;
            font-size: 0.7rem;
          }
          
          .stat-item.peak {
            display: none;
          }
          
          .nav-btn {
            width: 36px;
            height: 36px;
            font-size: 0.85rem;
            border-radius: 10px;
          }

          .main-content.with-chat .video-section {
            flex: 1;
          }

          .video-controls {
            padding: 10px 12px;
            gap: 8px;
            position: relative;
            background: rgba(0,0,0,0.8);
          }
          
          .controls-left {
            gap: 6px;
          }
          
          .controls-right {
            gap: 6px;
          }

          .ctrl-btn {
            width: 40px;
            height: 40px;
            font-size: 0.9rem;
          }
          
          .ctrl-btn.small {
            width: 34px;
            height: 34px;
            font-size: 0.8rem;
          }

          .go-live-btn, .end-btn {
            padding: 10px 18px;
            font-size: 0.85rem;
          }
          
          .go-live-btn span, .end-btn span {
            display: none;
          }
          
          .go-live-btn i, .end-btn i {
            margin: 0;
          }

          .volume-control {
            display: none;
          }

          .stream-info {
            padding: 10px;
            flex-wrap: wrap;
          }
          
          .streamer-info {
            gap: 10px;
          }
          
          .streamer-info .avatar {
            width: 36px;
            height: 36px;
          }
          
          .info-text h3 {
            font-size: 0.9rem;
          }
          
          .info-text .status {
            font-size: 0.75rem;
          }

          .stream-actions {
            gap: 6px;
          }
          
          .action-btn {
            padding: 8px 10px;
            font-size: 0.8rem;
          }
          
          .action-btn span {
            display: none;
          }
          
          .chat-section {
            width: 100%;
            max-width: 100%;
            border-radius: 0;
          }
          
          .chat-section.mobile {
            height: 50vh;
            max-height: 350px;
            border-radius: 16px 16px 0 0;
          }
          
          .chat-header {
            padding: 10px 12px;
          }
          
          .chat-tabs .tab {
            padding: 6px 10px;
            font-size: 0.75rem;
          }
          
          .chat-messages {
            padding: 10px;
            gap: 8px;
          }
          
          .message {
            padding: 10px;
            border-radius: 10px;
          }
          
          .msg-author {
            font-size: 0.8rem;
          }
          
          .msg-content {
            font-size: 0.85rem;
          }
          
          .reactions-bar {
            padding: 8px;
            gap: 4px;
          }
          
          .reaction-btn {
            width: 34px;
            height: 34px;
            font-size: 1rem;
          }
          
          .chat-form {
            padding: 10px;
          }
          
          .input-wrapper {
            padding: 4px;
            border-radius: 12px;
          }
          
          .input-wrapper input {
            font-size: 0.85rem;
          }
          
          .emoji-btn {
            width: 34px;
            height: 34px;
            font-size: 0.95rem;
          }
          
          .send-btn {
            width: 38px;
            height: 38px;
            font-size: 0.9rem;
            border-radius: 10px;
          }

          .modal {
            max-width: 100%;
            margin: 0;
            border-radius: 0;
            max-height: 100vh;
          }
          
          .modal-header {
            padding: 14px 16px;
          }
          
          .modal-header h2 {
            font-size: 1rem;
          }
          
          .modal-content {
            padding: 16px;
          }
          
          .modal-footer {
            padding: 14px 16px;
          }
          
          .setting-group label {
            font-size: 0.85rem;
          }
          
          .setting-group input,
          .setting-group select {
            padding: 12px;
            font-size: 0.9rem;
          }
          
          .btn {
            padding: 10px 18px;
            font-size: 0.85rem;
          }
          
          .chat-fab {
            width: 52px;
            height: 52px;
            font-size: 1.3rem;
            bottom: 16px;
            right: 16px;
          }
          
          .fab-badge {
            width: 20px;
            height: 20px;
            font-size: 0.65rem;
          }
          
          .camera-off p {
            font-size: 0.95rem;
          }
          
          .camera-icon {
            width: 90px;
            height: 90px;
            font-size: 2.2rem;
          }
          
          .live-overlay .stat {
            padding: 6px 10px;
            font-size: 0.7rem;
          }
          
          .live-overlay .stat.live-badge {
            padding: 6px 8px;
          }
        }

        /* Very Small Phones (< 375px) */
        @media (max-width: 374px) {
          .top-nav {
            padding: 6px 8px;
          }
          
          .logo img {
            height: 22px;
          }
          
          .live-indicator {
            padding: 3px 8px;
            font-size: 0.6rem;
          }
          
          .stat-item {
            padding: 3px 6px;
            font-size: 0.65rem;
            gap: 4px;
          }
          
          .nav-btn {
            width: 32px;
            height: 32px;
            font-size: 0.8rem;
          }
          
          .ctrl-btn {
            width: 36px;
            height: 36px;
            font-size: 0.85rem;
          }
          
          .go-live-btn, .end-btn {
            padding: 8px 14px;
            font-size: 0.8rem;
          }
          
          .stream-info {
            padding: 8px;
          }
          
          .streamer-info .avatar {
            width: 32px;
            height: 32px;
          }
          
          .info-text h3 {
            font-size: 0.85rem;
          }
          
          .action-btn {
            padding: 6px 8px;
            font-size: 0.75rem;
          }
          
          .chat-fab {
            width: 46px;
            height: 46px;
            font-size: 1.1rem;
            bottom: 12px;
            right: 12px;
          }
        }

        /* Safe Area for iPhone X+ */
        @supports (padding-top: env(safe-area-inset-top)) {
          .top-nav {
            padding-top: calc(8px + env(safe-area-inset-top));
          }
          
          .video-controls {
            padding-bottom: calc(10px + env(safe-area-inset-bottom));
          }
          
          .stream-info {
            padding-bottom: calc(10px + env(safe-area-inset-bottom));
          }
          
          .chat-fab {
            bottom: calc(16px + env(safe-area-inset-bottom));
          }
          
          .chat-section {
            padding-bottom: env(safe-area-inset-bottom);
          }
          
          /* Mobile chat specific */
          .main-content.mobile-layout .chat-form {
            padding: 10px;
            padding-bottom: calc(10px + env(safe-area-inset-bottom));
            flex-shrink: 0;
          }
          
          .main-content.mobile-layout .chat-messages {
            flex: 1;
            min-height: 0;
            overflow-y: auto;
          }
        }
      `;
